import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/content";
import BookingButton from "@/components/BookingButton";

const BY_SLUG = Object.fromEntries(BLOG_POSTS.map((p) => [p.slug, p]));

function setMeta(title, description) {
  if (typeof document === "undefined") return;
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", description);
}

export function BlogIndex() {
  useEffect(() => {
    setMeta(
      "Journal | Sano Acupuncture, Bronx NY",
      "Notes from the treatment room — acupuncture, nervous system regulation, Chinese medicine, and the rituals that hold us steady. By Joannie Campuzano, L.Ac."
    );
  }, []);

  return (
    <main className="min-h-[80vh]" data-testid="blog-index">
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-4">The Journal</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6 max-w-3xl">
            Notes from the treatment room.
          </h1>
          <p
            className="text-lg leading-relaxed max-w-2xl mb-14"
            style={{ color: "var(--sano-ink-2)" }}
          >
            Slow reads on acupuncture, nervous system regulation, the five
            elements, and the small daily rituals that hold a body together.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {BLOG_POSTS.map((p) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.slug}
                className="group block"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="overflow-hidden rounded-sm mb-5">
                  <img
                    src={p.cover}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div
                  className="flex gap-3 text-xs uppercase tracking-widest mb-3"
                  style={{ color: "var(--sano-ink-2)" }}
                >
                  <span>{p.date}</span>
                  <span>·</span>
                  <span>{p.minutes} min read</span>
                </div>
                <h2 className="font-serif-display text-2xl leading-tight mb-3">{p.title}</h2>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--sano-ink-2)" }}>
                  {p.excerpt}
                </p>
                <span
                  className="inline-flex items-center gap-1.5 text-sm"
                  style={{ color: "var(--sano-olive)" }}
                >
                  Read <ArrowUpRight size={14} strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BY_SLUG[slug];

  useEffect(() => {
    if (post) setMeta(`${post.title} | Sano Journal`, post.excerpt);
  }, [post]);

  if (!post) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <div className="eyebrow mb-4">Not found</div>
          <h1 className="font-serif-display text-4xl mb-4">That post doesn&apos;t exist.</h1>
          <Link
            to="/blog"
            className="text-sm border-b border-current pb-0.5"
            style={{ color: "var(--sano-olive)" }}
          >
            See all journal entries →
          </Link>
        </div>
      </main>
    );
  }

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <main className="min-h-[80vh]" data-testid={`blog-post-${post.slug}`}>
      <article>
        <header className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm mb-8"
              style={{ color: "var(--sano-olive)" }}
              data-testid="blog-back"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> The Journal
            </Link>
            <div
              className="flex gap-3 text-xs uppercase tracking-widest mb-5"
              style={{ color: "var(--sano-ink-2)" }}
            >
              <span>{post.date}</span>
              <span>·</span>
              <span>{post.minutes} min read</span>
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
              {post.title}
            </h1>
            <p
              className="text-lg leading-relaxed"
              style={{ color: "var(--sano-ink-2)" }}
            >
              {post.excerpt}
            </p>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 lg:px-12 mb-16">
          <img
            src={post.cover}
            alt={post.title}
            className="w-full h-[60vh] object-cover rounded-sm"
            data-testid="blog-cover"
          />
        </div>

        <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-20">
          <div className="space-y-7" data-testid="blog-body">
            {post.body?.map((para, i) => (
              <p
                key={i}
                className="text-lg leading-relaxed"
                style={{ color: "var(--sano-ink)" }}
              >
                {para}
              </p>
            ))}
          </div>

          <div
            className="mt-16 pt-10 border-t flex flex-wrap items-center justify-between gap-4"
            style={{ borderColor: "var(--sano-border)" }}
          >
            <p className="text-sm max-w-md" style={{ color: "var(--sano-ink-2)" }}>
              Reading is one thing. The body learns by experience. Come into the studio.
            </p>
            <BookingButton testid="blog-post-book-btn" />
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section
          className="py-20 border-t"
          style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
          data-testid="blog-more"
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="eyebrow mb-8">Keep reading</div>
            <div className="grid md:grid-cols-2 gap-10">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group block"
                  data-testid={`blog-more-${p.slug}`}
                >
                  <div className="overflow-hidden rounded-sm mb-5">
                    <img
                      src={p.cover}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-serif-display text-2xl leading-tight mb-2">{p.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
                    {p.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
